import { useSessionStorage } from 'hooks/useSessionStorage';
import * as React from 'react';
import { useCallback, useState } from 'react';
import Honeybadger from '@honeybadger-io/js';
import { useMutation } from 'react-apollo';
import { graphQLErrorMessage } from 'utilities/errors';
import { registerCustomCodeMutation } from '../graphql/registerCustomCode';
import { RegisterHomeTesting } from '../types';

export const STORAGE_KEY = 'KB-AT-HOME-CODE';
export const CUSTOM_CODE_REGEXP = /^K([B|T|X])([F|M])([A-Z0-9]{7})$/gm;

export const ERROR_MESSAGES: string | React.ReactNode = {
  // eslint-disable-next-line @typescript-eslint/camelcase
  invalid_custom_code:
    'Registration code incorrect. Please check for typos. If you are having trouble, contact Kindbody at 855-KND-BODY.',
  // eslint-disable-next-line @typescript-eslint/camelcase
  nonmatching_gender:
    'Registration code cannot be registered. Kit must match patient’s sex at birth. If you are having trouble, contact Kindbody at 855-KND-BODY.',
  // eslint-disable-next-line @typescript-eslint/camelcase
  custom_code_already_registered:
    'Registration code has already been registered. If you are having trouble, contact Kindbody at 855-KND-BODY.',
  // eslint-disable-next-line @typescript-eslint/camelcase
  empty_address: (
    <p>
      Your address is incomplete. Please fill out your address{' '}
      <a style={{ textDecoration: 'underline' }} href="/my-profile">
        here
      </a>
    </p>
  ),
  // eslint-disable-next-line @typescript-eslint/camelcase
  restricted_state:
    'Due to state regulations, we cannot process your lab test results. Please reach out to Kindbody at 855-KND-BODY if you have further questions.',
  // eslint-disable-next-line @typescript-eslint/camelcase
  international_country:
    'Due to country regulations, we cannot process lab test results for patients outside of the United States. Please reach out to Kindbody at +1 855-KND-BODY if you have further questions.'
};

export const useRegisterHomeTesting = () => {
  const [storedRegistrationCode, setStoredRegistrationCode] = useSessionStorage(STORAGE_KEY, '');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const [registerMutation] = useMutation<{
    registerHomeTesting: RegisterHomeTesting;
  }>(registerCustomCodeMutation);

  const registerHomeTesting = useCallback(
    async (registrationCode: string) => {
      setError('');
      setMessage('');

      if (!registrationCode.match(CUSTOM_CODE_REGEXP)) {
        setError(
          `Registration code incorrect. Please check for typos. If you are having trouble, contact Kindbody at 855-KND-BODY.`
        );
        return;
      }

      try {
        setLoading(true);

        const response = await registerMutation({
          variables: {
            customCode: registrationCode
          }
        });

        const { resultCode } = response.data.registerCustomCode;

        if (resultCode === 'custom_code_registered_successfully') {
          setMessage(
            "Your test kit is now registered. Please complete the test and send it back using the instructions included in your test kit box . You'll receive an email when your results are ready. Snap a pic of your USPS return tracking number so that you can track your sample to the lab!"
          );
          setStoredRegistrationCode('');

          return;
        }

        if (ERROR_MESSAGES[resultCode]) {
          setError(ERROR_MESSAGES[resultCode]);
        } else {
          setError(
            'Something went wrong! If you are having trouble, contact Kindbody at 855-KND-BODY.'
          );
        }

        setStoredRegistrationCode(registrationCode);
      } catch (error) {
        Honeybadger.notify(error);
        const errorMessage = graphQLErrorMessage(error) || 'Something went wrong.';
        setError(errorMessage);

        setStoredRegistrationCode(registrationCode);
      } finally {
        setLoading(false);
      }
    },
    [registerMutation, setStoredRegistrationCode]
  );

  return {
    storedRegistrationCode,
    error,
    message,
    loading,
    registerHomeTesting
  };
};
